import { Workbook } from 'exceljs';
import type { Style, Buffer, Worksheet } from 'exceljs';
import type { Row, Table } from 'src/app/models/ui/table';
import type { MrTranslatePipe } from 'src/app/pipes/mr-translate.pipe';
import type { ObjektTypModel } from './systemmanagementdialog.component';
import type { BackendLocaleDatePipe } from 'src/app/pipes/get-locale-date.pipe';

const SYSTEM_ROWS = { start: 1, end: 6, length: 6 } as const;
const HEADER_ROWS = [2, 3] as const;
const MAX_INSERT_ROWS = 30_000;

//#region Übersicht
export async function createExcel_SpezifikationListe(
  typen: ObjektTypModel[],
  res: {}
): Promise<{ url: string; filename: string }> {
  assertSpezifikationTablesExport(res);
  const wb = new Workbook();

  typen.forEach(typ => {
    const ws = wb.addWorksheet(typ.BEZEICHNUNG, {
      properties: { defaultColWidth: 15 },
      views: [{ state: 'frozen', xSplit: 0, ySplit: 1 }],
    });

    const { columns, rows } = res.spezTables[typ.ID];

    columns.forEach((col, index) => {
      const wc = ws.getColumn(index + 1);
      wc.key = col.id;
      wc.header = [col.id];
    });

    ws.addRows(rows);

    const argb = 'd4e7e2';
    const border = { color: { argb }, style: 'medium' } as const;

    const headerRow = ws.getRow(1);
    headerRow.border = { bottom: border, top: border };
    headerRow.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb } };

    ws.getRows(2, rows.length).forEach((row, index) => {
      row.border = { bottom: border };
      if (index % 2 == 1) row.fill = { type: 'pattern', pattern: 'lightGray', fgColor: { argb } };
    });

    ws.autoFilter = {
      from: { row: 1, column: 1 },
      to: { row: rows.length, column: columns.length },
    };

    ws.columns.forEach(col => {
      let maxWidth = 0;
      col.eachCell(cell => {
        const width = cell.value?.toString().length;
        if (width > maxWidth) maxWidth = width;
      });

      col.width = Math.max(maxWidth, 15);
    });
  });

  const buffer = await wb.xlsx.writeBuffer();
  let allNames = typen
    .slice(0, 3)
    .map(typ => typ.BEZEICHNUNG)
    .join(', ');
  if (typen.length > 4) allNames += ' und weitere';
  return { url: bufferToUrl(buffer), filename: `Spezifikationen ${allNames}.xlsx` };
}

interface SpezifikationTablesExport {
  spezTables: Record<number, Table>;
}

function assertSpezifikationTablesExport(res: {}): asserts res is SpezifikationTablesExport {
  let valid =
    'spezTables' in res &&
    typeof res.spezTables == 'object' &&
    Object.values<unknown>(res.spezTables as {}).every(
      table =>
        typeof table == 'object' &&
        'columns' in table &&
        typeof table.columns == 'object' &&
        Array.isArray(table.columns) &&
        'rows' in table &&
        typeof table.rows == 'object' &&
        Array.isArray(table.rows)
    );

  if (!valid) throw new Error('Invalid data');
}
//#endregion

//#region Vorlage
export async function exportTableInExcel(
  typ: unknown,
  res: {},
  mode: 'Stammdaten' | 'Spezifikation',
  translator: MrTranslatePipe,
  localeDate: BackendLocaleDatePipe
): Promise<{ url: string; filename: string }> {
  assertStammdatenVorlageExport(res);

  const { ID, BEZEICHNUNG, NAME } = typ as ObjektTypModel;
  const { columns, rows } = res;

  const userRows = transformRows(rows, columns, localeDate);

  const USER_ROWS = {
    start: SYSTEM_ROWS.end + 1,
    length: userRows.length,
  } as const;

  const { ws, wb } = createExcel(NAME, ID, BEZEICHNUNG);
  const headerBlocks: Record<string, { start: number; end: number }> = {};

  columns.forEach((col, i) => {
    const excelIndex = i + 1;
    setKeyAndSystemValuesForColumn(ws, excelIndex, col, translator);

    const block = headerBlocks[col.Header];
    if (block) block.end = excelIndex;
    else headerBlocks[col.Header] = { start: excelIndex, end: excelIndex };

    fillColorInSystemRows(ws, col);
  });

  ws.addRows(userRows);

  setObjekttypDataInFirstRow(ws, mode, BEZEICHNUNG, ID, translator);
  mergeHeaderBlocksAndHideSystemHeader(ws, headerBlocks);
  setFilterAndUnlockUserRows(ws, columns.length, USER_ROWS);
  if (rows) ws.getColumn('OSTAMMID').protection = { locked: true };
  if (mode == 'Spezifikation') ws.getColumn('OSPEZID').protection = { locked: true };

  return await protectAndWriteExcel(ws, wb, `${mode} ${rows ? 'Bearbeiten' : 'Vorlage'} ${BEZEICHNUNG}.xlsx`);
}

interface StammdatenVorlageExport {
  columns: ExportColumn[];
  rows: Row[];
}

function assertStammdatenVorlageExport(res: {}): asserts res is StammdatenVorlageExport {
  let valid =
    'columns' in res &&
    typeof res.columns == 'object' &&
    Array.isArray(res.columns) &&
    res.columns.every(col => typeof col == 'object');
  if ('rows' in res)
    valid &&= typeof res.rows == 'object' && Array.isArray(res.rows) && res.rows.every(row => typeof row == 'object');

  if (!valid) throw new Error('Invalid data');
}
//#endregion

//#region Shared
function transformRows(rows: Row[], columns: ExportColumn[], localeDate: BackendLocaleDatePipe) {
  const userRows = rows ?? Array.from({ length: MAX_INSERT_ROWS }, () => ({}) as Row);
  for (const { DBField, IsDatum } of columns) {
    if (!rows) userRows.forEach(row => (row[DBField] = null));
    else if (IsDatum) userRows.forEach(row => (row[DBField] = localeDate.toDateFromBackend(row[DBField] as string)));
  }

  return userRows;
}

function createExcel(NAME: string, ID: number, BEZEICHNUNG: string) {
  const wb = new Workbook();
  const ws = wb.addWorksheet(BEZEICHNUNG, {
    views: [{ state: 'frozen', xSplit: 0, ySplit: SYSTEM_ROWS.length }],
    headerFooter: { oddHeader: `ID: ${ID}; NAME: ${NAME}` },
    properties: { defaultColWidth: 20 },
  });
  return { ws, wb };
}

function setKeyAndSystemValuesForColumn(ws: Worksheet, index: number, col: ExportColumn, translator: MrTranslatePipe) {
  const wc = ws.getColumn(index);
  wc.key = col.DBField;
  wc.width = getMaxWidth(col);
  wc.values = [
    /* skip first row */ null,
    translator.transform(col.Header),
    col.Header,
    col.DBField,
    col.IsPflicht ? '✔' : '',
    col.Name,
  ];
}

function fillColorInSystemRows(ws: Worksheet, col: ExportColumn) {
  ws.getRows(SYSTEM_ROWS.start + 1, SYSTEM_ROWS.length - 1).forEach(row => {
    row.getCell(col.DBField).style = getFillColor(col.Color);
  });
}

function setObjekttypDataInFirstRow(
  ws: Worksheet,
  mode: 'Stammdaten' | 'Spezifikation',
  bez: string,
  OTYPID: number,
  translator: MrTranslatePipe
) {
  const FIRST_ROW = ws.getRow(1);
  let cell = FIRST_ROW.getCell('A');
  ws.mergeCells('A1:D1');
  cell.value = translator.transform(`${mode}import`);
  cell.style = getFillColor('FFF2CC');
  cell.font = { bold: true, size: 24 };

  cell = FIRST_ROW.getCell('E');
  ws.mergeCells('E1:H1');
  cell.value = bez;
  cell.style = getFillColor('FFF2CC');
  cell.font = { bold: true, size: 20 };

  cell = FIRST_ROW.getCell('I');
  cell.value = OTYPID;
}

function mergeHeaderBlocksAndHideSystemHeader(
  ws: Worksheet,
  headerBlocks: Record<string, { start: number; end: number }>
) {
  for (const { start, end } of Object.values(headerBlocks)) {
    HEADER_ROWS.forEach(row => {
      ws.mergeCells(row, start, row, end);
      ws.getCell(row, start).font = { bold: true, size: 18 };
    });
  }
  ws.getRow(3).hidden = true;
}

function setFilterAndUnlockUserRows(ws: Worksheet, maxCol: number, USER_ROWS: { start: number; length: number }) {
  ws.autoFilter = {
    from: { row: USER_ROWS.start - 1, column: 1 },
    to: { row: USER_ROWS.length, column: maxCol },
  };
  ws.getRows(USER_ROWS.start, USER_ROWS.length).forEach(row => (row.protection = { locked: false }));
}

function getMaxWidth(col: ExportColumn): number {
  return Math.max(col.Name.length + 1, col.DBField.length + 1, 15);
}

function getFillColor(color: string): Partial<Style> {
  return {
    fill: {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: color },
    },
  };
}

async function protectAndWriteExcel(ws: Worksheet, wb: Workbook, filename: string) {
  await ws.protect('mr.pro', {
    autoFilter: true,
    formatRows: true,
    formatColumns: true,
    formatCells: true,
    sort: true,
  });
  const buffer = await wb.xlsx.writeBuffer();
  return { url: bufferToUrl(buffer), filename };
}

function bufferToUrl(buffer: Buffer): string {
  const blob = new Blob([buffer], { type: 'application/octet-stream' });
  return URL.createObjectURL(blob);
}

interface ExportColumn {
  Header: string;
  Color: string;
  DBField: string;
  Name: string;
  IsPflicht: boolean;
  IsDatum: boolean;
}
//#endregion
